/**
 * Theme setup and management for DevBar.
 *
 * Extracted from GlobalDevBar to reduce file size.
 */

import { getEffectiveTheme, getTheme, injectThemeCSS, setStoredThemeMode } from '../constants.js';
import type { ThemeMode } from '../types.js';
import type { DevBarState } from './types.js';

export const RESPONSIVE_COMPACT_MAX_WIDTH = 860;

export function resolveCompactModeForViewport(compactMode: boolean): boolean {
  if (compactMode) return true;
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= RESPONSIVE_COMPACT_MAX_WIDTH;
}

/**
 * Setup the theme system: load stored preference and listen for system changes.
 */
export function setupTheme(state: DevBarState): void {
  // Load stored theme preference from settings manager
  const settings = state.settingsManager.getSettings();
  state.themeMode = state.forcedThemeMode ?? settings.themeMode;
  if (state.forcedThemeMode && settings.themeMode !== state.forcedThemeMode) {
    state.settingsManager.saveSettingsNow({ themeMode: state.forcedThemeMode });
  }
  // Inject the appropriate theme CSS variables on initial load
  injectThemeCSS(getTheme(state.themeMode));
  state.debug.state('Theme loaded', { mode: state.themeMode });

  // Listen for system theme changes
  if (typeof window !== 'undefined' && window.matchMedia) {
    state.themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    state.themeMediaHandler = () => {
      if (state.themeMode === 'system') {
        // Re-inject theme CSS when system preference changes
        injectThemeCSS(getTheme(state.themeMode));
        state.debug.state('System theme changed', {
          effectiveTheme: getEffectiveTheme(state.themeMode),
        });
        // Dispatch event so host apps can respond
        window.dispatchEvent(
          new CustomEvent('devbar-theme-change', { detail: { mode: state.themeMode } })
        );
        state.render();
      }
    };
    state.themeMediaQuery.addEventListener('change', state.themeMediaHandler);
  }
}

/**
 * Load compact mode setting from storage.
 */
export function loadCompactMode(state: DevBarState): void {
  const settings = state.settingsManager.getSettings();
  state.compactMode = resolveCompactModeForViewport(settings.compactMode);
  state.debug.state('Compact mode loaded', { compactMode: state.compactMode });
}

/**
 * Set the theme mode, persist it, inject CSS, and dispatch event.
 */
export function setThemeMode(state: DevBarState, mode: ThemeMode): void {
  const nextMode = state.forcedThemeMode ?? mode;
  state.themeMode = nextMode;
  state.settingsManager.saveSettings({ themeMode: nextMode });
  // Also update legacy storage key for backwards compatibility with getStoredThemeMode()
  setStoredThemeMode(nextMode);
  // Inject the appropriate theme CSS variables
  injectThemeCSS(getTheme(nextMode));
  state.debug.state('Theme mode changed', {
    mode: nextMode,
    effectiveTheme: getEffectiveTheme(nextMode),
  });
  // Dispatch custom event so host apps can respond to theme changes
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('devbar-theme-change', { detail: { mode: nextMode } }));
  }
  state.render();
}
